import { z } from "zod";

import {
  createManuscriptFigure,
  createReference,
  createSectionForParent,
  insertChildSection,
  insertSectionAfterTree,
  type Manuscript,
  type StructuredAbstractKey,
} from "./manuscript.ts";
import { normalizeTemplate, type ManuscriptTemplate } from "./templates.ts";

export const AI_FORMAT_PATHS = [
  "page.pageNumbers",
  "page.lineNumbers",
  "page.lineNumberMode",
  "page.lineNumberStart",
  "page.lineNumberInterval",
  "typography.latinFont",
  "typography.eastAsiaFont",
  "typography.bodySizePt",
  "typography.titleSizePt",
  "typography.h1SizePt",
  "typography.h2SizePt",
  "typography.h3SizePt",
  "typography.figureCaptionSizePt",
  "paragraph.lineSpacing",
  "paragraph.firstLineIndentMm",
  "paragraph.spaceBeforePt",
  "paragraph.spaceAfterPt",
  "paragraph.alignment",
  "blockStyles.abstract.lineSpacing",
  "blockStyles.abstract.firstLineIndentMm",
  "blockStyles.abstract.spaceBeforePt",
  "blockStyles.abstract.spaceAfterPt",
  "blockStyles.abstract.alignment",
  "blockStyles.h1.lineSpacing",
  "blockStyles.h1.firstLineIndentMm",
  "blockStyles.h1.spaceBeforePt",
  "blockStyles.h1.spaceAfterPt",
  "blockStyles.h1.alignment",
  "blockStyles.h2.lineSpacing",
  "blockStyles.h2.firstLineIndentMm",
  "blockStyles.h2.spaceBeforePt",
  "blockStyles.h2.spaceAfterPt",
  "blockStyles.h2.alignment",
  "blockStyles.h3.lineSpacing",
  "blockStyles.h3.firstLineIndentMm",
  "blockStyles.h3.spaceBeforePt",
  "blockStyles.h3.spaceAfterPt",
  "blockStyles.h3.alignment",
  "blockStyles.figureCaption.lineSpacing",
  "blockStyles.figureCaption.firstLineIndentMm",
  "blockStyles.figureCaption.spaceBeforePt",
  "blockStyles.figureCaption.spaceAfterPt",
  "blockStyles.figureCaption.alignment",
  "references.numbering",
  "references.hangingIndentMm",
  "references.lineSpacing",
  "references.spaceAfterPt",
  "numbering",
] as const;

export type AiFormatPath = (typeof AI_FORMAT_PATHS)[number];

const bounded = (maximum: number) => z.string().max(maximum);
const aiActionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("set_submission_route"),
    value: z.enum(["generic", "journal"]),
  }),
  z.object({
    type: z.literal("set_metadata"),
    field: z.enum(["title", "runningTitle", "journal", "articleType"]),
    value: bounded(1000),
  }),
  z.object({
    type: z.literal("set_keywords"),
    value: bounded(10_000),
  }),
  z.object({
    type: z.literal("set_abstract_mode"),
    value: z.enum(["structured", "unstructured"]),
  }),
  z.object({
    type: z.literal("set_structured_abstract"),
    field: z.enum(["background", "materialsAndMethods", "results", "conclusion"]),
    value: bounded(50_000),
  }),
  z.object({
    type: z.literal("set_unstructured_abstract"),
    value: bounded(100_000),
  }),
  z.object({
    type: z.literal("add_section"),
    parentId: z.string().max(160).nullable().optional(),
    title: bounded(1000),
    content: bounded(500_000).optional().default(""),
  }),
  z.object({
    type: z.literal("update_section"),
    id: z.string().min(1).max(160),
    title: bounded(1000).optional(),
    content: bounded(500_000).optional(),
  }),
  z.object({
    type: z.literal("add_reference"),
    text: bounded(30_000),
    doi: bounded(500).optional().default(""),
    pmid: bounded(500).optional().default(""),
    url: bounded(3000).optional().default(""),
  }),
  z.object({
    type: z.literal("add_figure"),
    title: bounded(2000),
    legend: bounded(50_000).optional().default(""),
  }),
  z.object({
    type: z.literal("update_figure"),
    id: z.string().min(1).max(160),
    title: bounded(2000).optional(),
    legend: bounded(50_000).optional(),
  }),
  z.object({
    type: z.literal("set_format"),
    path: z.enum(AI_FORMAT_PATHS),
    value: z.union([z.string(), z.number().finite(), z.boolean()]),
  }),
  z.object({
    type: z.literal("set_emphasis_rules"),
    style: z.enum(["bold", "italic", "boldItalic"]),
    terms: z.array(z.string().trim().min(1).max(200)).max(200),
  }),
]);

export const aiControlPlanSchema = z.object({
  version: z.literal(1),
  actions: z.array(aiActionSchema).min(1).max(100),
});

export type AiControlPlan = z.infer<typeof aiControlPlanSchema>;
export type AiControlAction = AiControlPlan["actions"][number];

export type AiControlResult = {
  manuscript: Manuscript;
  template: ManuscriptTemplate;
  applied: number;
  messages: string[];
};

const formatEnums: Partial<Record<AiFormatPath, readonly string[]>> = {
  "page.lineNumberMode": ["continuous", "restartPage", "restartSection"],
  "paragraph.alignment": ["left", "justify"],
  "blockStyles.abstract.alignment": ["left", "center", "right", "justify"],
  "blockStyles.h1.alignment": ["left", "center", "right", "justify"],
  "blockStyles.h2.alignment": ["left", "center", "right", "justify"],
  "blockStyles.h3.alignment": ["left", "center", "right", "justify"],
  "blockStyles.figureCaption.alignment": ["left", "center", "right", "justify"],
  "references.numbering": ["preserve", "numeric"],
  numbering: ["none", "decimal"],
};

const formatNumberRanges: Partial<
  Record<AiFormatPath, { minimum: number; maximum: number; integer?: boolean }>
> = {
  "page.lineNumberStart": { minimum: 1, maximum: 9999, integer: true },
  "page.lineNumberInterval": { minimum: 1, maximum: 20, integer: true },
  "typography.bodySizePt": { minimum: 8, maximum: 18 },
  "typography.titleSizePt": { minimum: 12, maximum: 32 },
  "typography.h1SizePt": { minimum: 9, maximum: 24 },
  "typography.h2SizePt": { minimum: 8, maximum: 22 },
  "typography.h3SizePt": { minimum: 8, maximum: 20 },
  "typography.figureCaptionSizePt": { minimum: 8, maximum: 16 },
  "paragraph.lineSpacing": { minimum: 1, maximum: 3 },
  "paragraph.firstLineIndentMm": { minimum: 0, maximum: 20 },
  "paragraph.spaceBeforePt": { minimum: 0, maximum: 36 },
  "paragraph.spaceAfterPt": { minimum: 0, maximum: 36 },
  "blockStyles.abstract.lineSpacing": { minimum: 1, maximum: 3 },
  "blockStyles.abstract.firstLineIndentMm": { minimum: 0, maximum: 20 },
  "blockStyles.abstract.spaceBeforePt": { minimum: 0, maximum: 36 },
  "blockStyles.abstract.spaceAfterPt": { minimum: 0, maximum: 36 },
  "blockStyles.h1.lineSpacing": { minimum: 1, maximum: 3 },
  "blockStyles.h1.firstLineIndentMm": { minimum: 0, maximum: 20 },
  "blockStyles.h1.spaceBeforePt": { minimum: 0, maximum: 36 },
  "blockStyles.h1.spaceAfterPt": { minimum: 0, maximum: 36 },
  "blockStyles.h2.lineSpacing": { minimum: 1, maximum: 3 },
  "blockStyles.h2.firstLineIndentMm": { minimum: 0, maximum: 20 },
  "blockStyles.h2.spaceBeforePt": { minimum: 0, maximum: 36 },
  "blockStyles.h2.spaceAfterPt": { minimum: 0, maximum: 36 },
  "blockStyles.h3.lineSpacing": { minimum: 1, maximum: 3 },
  "blockStyles.h3.firstLineIndentMm": { minimum: 0, maximum: 20 },
  "blockStyles.h3.spaceBeforePt": { minimum: 0, maximum: 36 },
  "blockStyles.h3.spaceAfterPt": { minimum: 0, maximum: 36 },
  "blockStyles.figureCaption.lineSpacing": { minimum: 1, maximum: 3 },
  "blockStyles.figureCaption.firstLineIndentMm": { minimum: 0, maximum: 20 },
  "blockStyles.figureCaption.spaceBeforePt": { minimum: 0, maximum: 36 },
  "blockStyles.figureCaption.spaceAfterPt": { minimum: 0, maximum: 36 },
  "references.hangingIndentMm": { minimum: 0, maximum: 20 },
  "references.lineSpacing": { minimum: 1, maximum: 3 },
  "references.spaceAfterPt": { minimum: 0, maximum: 36 },
};

function assertFormatValue(
  path: AiFormatPath,
  value: string | number | boolean,
): void {
  if (path === "page.pageNumbers" || path === "page.lineNumbers") {
    if (typeof value !== "boolean") throw new Error(`${path} 必须是布尔值`);
    return;
  }
  if (path === "typography.latinFont" || path === "typography.eastAsiaFont") {
    if (typeof value !== "string" || !value.trim() || value.length > 120) {
      throw new Error(`${path} 必须是有效字体名称`);
    }
    return;
  }
  const allowed = formatEnums[path];
  if (allowed) {
    if (typeof value !== "string" || !allowed.includes(value)) {
      throw new Error(`${path} 的值不在允许范围内`);
    }
    return;
  }
  const range = formatNumberRanges[path];
  if (
    !range ||
    typeof value !== "number" ||
    value < range.minimum ||
    value > range.maximum ||
    (range.integer && !Number.isInteger(value))
  ) {
    throw new Error(`${path} 的数值不在允许范围内`);
  }
}

function setFormatValue(
  template: ManuscriptTemplate,
  path: AiFormatPath,
  value: string | number | boolean,
): ManuscriptTemplate {
  assertFormatValue(path, value);
  const next = structuredClone(template) as ManuscriptTemplate &
    Record<string, unknown>;
  const keys = path.split(".");
  let target = next as unknown as Record<string, unknown>;
  for (const key of keys.slice(0, -1)) {
    target = target[key] as Record<string, unknown>;
  }
  target[keys.at(-1) ?? path] = value;
  return normalizeTemplate(next);
}

export function applyAiControlPlan(
  manuscript: Manuscript,
  template: ManuscriptTemplate,
  input: unknown,
): AiControlResult {
  const plan = aiControlPlanSchema.parse(input);
  let nextManuscript = structuredClone(manuscript);
  let nextTemplate = structuredClone(template);
  const messages: string[] = [];

  for (const action of plan.actions) {
    switch (action.type) {
      case "set_submission_route":
        nextManuscript.submissionRoute = action.value;
        if (action.value === "generic") nextManuscript.metadata.journal = "";
        messages.push(
          action.value === "generic"
            ? "已切换为通用 SCI 路线"
            : "已切换为指定期刊路线",
        );
        break;
      case "set_metadata": {
        nextManuscript.metadata[action.field] = action.value;
        if (action.field === "journal" && action.value.trim()) {
          nextManuscript.submissionRoute = "journal";
        }
        if (action.field === "title" && action.value.trim()) {
          nextManuscript.name = action.value.trim().slice(0, 60);
        }
        messages.push(`已更新 ${action.field}`);
        break;
      }
      case "set_keywords":
        nextManuscript.keywords = action.value;
        messages.push("已更新关键词");
        break;
      case "set_abstract_mode":
        nextManuscript.abstract.mode = action.value;
        messages.push(`摘要模式已设为 ${action.value}`);
        break;
      case "set_structured_abstract":
        nextManuscript.abstract.structured[
          action.field as StructuredAbstractKey
        ] = action.value;
        messages.push(`已更新结构式摘要 ${action.field}`);
        break;
      case "set_unstructured_abstract":
        nextManuscript.abstract.content = action.value;
        messages.push("已更新非结构式摘要");
        break;
      case "add_section": {
        const baseSection = createSectionForParent(
          nextManuscript.sections,
          action.parentId ?? null,
          action.title,
        );
        if (!baseSection) {
          if (action.parentId) {
            const parent = nextManuscript.sections.find(
              (item) => item.id === action.parentId,
            );
            if (!parent) throw new Error(`找不到上级标题：${action.parentId}`);
          }
          throw new Error("三级标题下面不能继续添加标题");
        }
        const level = baseSection.level;
        const section = {
          ...baseSection,
          content: action.content,
        };
        nextManuscript.sections = section.parentId
          ? insertChildSection(nextManuscript.sections, section)
          : insertSectionAfterTree(nextManuscript.sections, section);
        messages.push(`已添加 ${level} 级标题“${action.title}”`);
        break;
      }
      case "update_section": {
        const index = nextManuscript.sections.findIndex(
          (section) => section.id === action.id,
        );
        if (index < 0) throw new Error(`找不到标题：${action.id}`);
        nextManuscript.sections[index] = {
          ...nextManuscript.sections[index],
          ...(action.title === undefined ? {} : { title: action.title }),
          ...(action.content === undefined ? {} : { content: action.content }),
        };
        messages.push(`已更新标题 ${action.id}`);
        break;
      }
      case "add_reference":
        nextManuscript.references.push({
          ...createReference(),
          text: action.text,
          doi: action.doi,
          pmid: action.pmid,
          url: action.url,
        });
        messages.push("已添加 1 条参考文献");
        break;
      case "add_figure":
        nextManuscript.figures.push({
          ...createManuscriptFigure(),
          title: action.title,
          legend: action.legend,
        });
        messages.push("已添加 1 条图注");
        break;
      case "update_figure": {
        const figureIndex = nextManuscript.figures.findIndex(
          (figure) => figure.id === action.id,
        );
        if (figureIndex < 0) {
          throw new Error(`找不到图注：${action.id}`);
        }
        nextManuscript.figures[figureIndex] = {
          ...nextManuscript.figures[figureIndex],
          ...(action.title === undefined ? {} : { title: action.title }),
          ...(action.legend === undefined ? {} : { legend: action.legend }),
        };
        messages.push(`已更新图注 ${action.id}`);
        break;
      }
      case "set_format":
        nextTemplate = setFormatValue(nextTemplate, action.path, action.value);
        messages.push(`已设置 ${action.path}`);
        break;
      case "set_emphasis_rules":
        nextTemplate = normalizeTemplate({
          ...nextTemplate,
          emphasisRules: {
            ...nextTemplate.emphasisRules,
            [action.style]: action.terms,
          },
        });
        messages.push(`已更新 ${action.style} 文字强调规则`);
        break;
    }
  }

  nextManuscript.updatedAt = new Date().toISOString();
  return {
    manuscript: nextManuscript,
    template: nextTemplate,
    applied: plan.actions.length,
    messages,
  };
}

export const AI_CONTROL_EXAMPLE: AiControlPlan = {
  version: 1,
  actions: [
    { type: "set_format", path: "paragraph.lineSpacing", value: 2 },
    { type: "set_format", path: "page.lineNumbers", value: true },
    {
      type: "set_format",
      path: "page.lineNumberMode",
      value: "continuous",
    },
    {
      type: "set_emphasis_rules",
      style: "italic",
      terms: ["Arabidopsis thaliana"],
    },
  ],
};
